import { useEffect, useRef } from 'react';
import {
  GLOSS_KEY,
  GLOSS_SCALE_MAX,
  GLOSS_SCALE_MIN,
  GLOSS_SCALE_STEP,
} from '../lib/constants';
import type { Dialect, Mode } from '../lib/types';

interface SettingsProps {
  mode: Mode;
  englishOpen: boolean;
  dialect: Dialect;
  glossScale: number;
  onSetMode(mode: Mode): void;
  onSetEnglishOpen(open: boolean): void;
  onSetDialect(dialect: Dialect): void;
  onSetGlossScale(scale: number): void;
  onClose(): void;
}

const MODE_OPTIONS: { value: Mode; label: string; description: string }[] = [
  {
    value: 'Learner',
    label: 'LEARNER',
    description: 'Gloss large, grammar notes on, English one tap away.',
  },
  {
    value: 'Deaf-first',
    label: 'DEAF-FIRST',
    description: 'Signing and gloss only. No notes, English stays folded.',
  },
];

const ENGLISH_OPTIONS: { value: boolean; label: string }[] = [
  { value: false, label: 'HIDDEN BY DEFAULT' },
  { value: true, label: 'SHOWN BY DEFAULT' },
];

const DIALECTS: Dialect[] = ['Southern', 'Northern'];

export function Settings({
  mode,
  englishOpen,
  dialect,
  glossScale,
  onSetMode,
  onSetEnglishOpen,
  onSetDialect,
  onSetGlossScale,
  onClose,
}: SettingsProps) {
  const done = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    done.current?.focus();
  }, []);

  return (
    <div
      className="settings"
      role="dialog"
      aria-modal="true"
      aria-label="Settings"
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
    >
      <div className="pad-top" />
      <div className="settings-head">
        <h2 className="wordmark">SETTINGS</h2>
        <button type="button" className="btn-primary" onClick={onClose} ref={done}>
          DONE
        </button>
      </div>

      <div className="settings-body">
        <section className="settings-section">
          <p className="kicker settings-kicker" id="settings-primary-user">
            Primary user
          </p>
          <div role="radiogroup" aria-labelledby="settings-primary-user">
            {MODE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                className="option-card"
                aria-checked={mode === option.value}
                onClick={() => onSetMode(option.value)}
              >
                <span className="option-card-label">{option.label}</span>
                <span className="option-card-desc">{option.description}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="settings-section">
          <p className="kicker settings-kicker" id="settings-english">
            English backend
          </p>
          <div role="radiogroup" aria-labelledby="settings-english">
            {ENGLISH_OPTIONS.map((option) => (
              <button
                key={option.label}
                type="button"
                role="radio"
                className="option-row"
                aria-checked={englishOpen === option.value}
                onClick={() => onSetEnglishOpen(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className="settings-note">
            Auslan always leads. English is a reference layer, never the reply.
          </p>
        </section>

        <section className="settings-section">
          <p className="kicker settings-kicker" id="settings-dialect">
            Dialect
          </p>
          <div role="radiogroup" aria-labelledby="settings-dialect">
            {DIALECTS.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                className="option-row"
                aria-checked={dialect === option}
                onClick={() => onSetDialect(option)}
              >
                {option.toUpperCase()}
              </button>
            ))}
          </div>
          <p className="settings-note">
            Auslan has real regional variation — Southern (Melbourne, Adelaide, Hobart, Perth) and
            Northern (Sydney, Brisbane) signs differ, especially numbers and colours.
          </p>
        </section>

        <section className="settings-section">
          <p className="kicker settings-kicker" id="settings-gloss-size">
            Gloss size
          </p>
          <div className="gloss-size">
            <input
              type="range"
              aria-labelledby="settings-gloss-size"
              min={GLOSS_SCALE_MIN}
              max={GLOSS_SCALE_MAX}
              step={GLOSS_SCALE_STEP}
              value={glossScale}
              onChange={(event) => onSetGlossScale(Number(event.target.value))}
            />
            <span className="gloss-size-value">{Math.round(glossScale * 100)}%</span>
          </div>
          <p className="settings-note">
            Gloss is the reply, so it has its own size control on top of the OS text size.
          </p>
        </section>

        <section className="settings-section">
          <p className="kicker settings-kicker">Gloss key</p>
          <dl>
            {GLOSS_KEY.map((entry) => (
              <div className="legend-row" key={entry.key}>
                <dt className="legend-key">{entry.key}</dt>
                <dd className="legend-value">{entry.meaning}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="settings-inline">
          <div>
            <p className="settings-inline-title">Camera sign input</p>
            <p className="settings-note">
              Sign to the camera and have it read back. Not wired in this demo.
            </p>
          </div>
          <p className="tag-outline">SOON</p>
        </div>

        <section className="settings-section">
          <p className="kicker settings-kicker">Accuracy</p>
          <p className="settings-note">
            Gloss here is produced by a language model. It is a learning aid, not certified
            interpretation. Vocabulary, dialect and grammar have not yet been reviewed by a Deaf
            Auslan consultant — do not rely on it for medical, legal or emergency communication.
          </p>
        </section>
      </div>
    </div>
  );
}
